import { DateValue } from '@internationalized/date';
import { PlusIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty, omitBy } from 'lodash/fp';

import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_CASES_PAGE } from 'dashboard/components/DisabledProductPage/config';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { BulkSetCaseStateData } from 'dashboard/forms/FormBulkSetCaseState';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { Case, CaseStatus } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseState, CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { ExporterFileType } from 'dashboard/models/exporter';
import { Organization } from 'dashboard/models/organization';
import { CasesTableView } from 'dashboard/models/table-view';
import { Tag } from 'dashboard/models/tag';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import { UserNotificationStatus } from 'dashboard/models/user-notification';
import { CasesFilterParams, FilterValues } from 'dashboard/redux/actions/cases';
import { UserNotificationsDetails } from 'dashboard/redux/slices/user-notifications';
import CasesCreateCaseModal, {
  CreateCaseFormData,
} from 'dashboard/views/DashboardCasesView/CasesCreateCaseModal';
import ConfigurableCasesTable from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable';
import {
  ARCHIVED_REQUEST_KEY,
  INBOX_REQUEST_KEY,
  RequestKey,
} from 'dashboard/views/DashboardCasesView/constants';
import UserNotifications from 'dashboard/views/DashboardCasesView/UserNotifications';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import { SegmentClient } from 'lib/clients/SegmentClient';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { Iso8601DateString } from 'lib/types';

type TableViewConfiguration = CasesTableView['attributes']['configuration'];

export const MAX_EXPORT_COUNT_LIMIT = 100_000;

export interface BulkActionCaseInfo {
  caseId: string;
  status: CaseStatus;
  state: CaseState;
  caseTemplateId: string;
}

export interface DashboardCasesViewProps {
  loadingCases: boolean;
  loadingTableViews: boolean;
  loadingTags: boolean;
  loadingCaseTemplateBasicsOptions: boolean;
  productFeatureIsDisabled: boolean;
  exportersProductFeatureIsDisabled: boolean;
  accounts: Account[];
  allTags: Tag[];
  casesTags: Tag[];
  caseQueues: CaseQueue[];
  activeTeams: Team[];
  users: User[];
  userNotifications: {
    [INBOX_REQUEST_KEY]: UserNotificationsDetails;
    [ARCHIVED_REQUEST_KEY]: UserNotificationsDetails;
  };
  cases: Case[];
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseUsers: User[];
  caseCaseQueues: CaseQueue[];
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: CasesFilterParams;
  pageSize: number;
  organization: Organization;
  currentTableView: CasesTableView;
  activeTableViews: CasesTableView[];
  impersonationMode: string;
  hasCasesFree: boolean;
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  handleCreateCase: (values: CreateCaseFormData) => Promise<void>;
  handleCreateTableView: (name: string, configuration: TableViewConfiguration) => void;
  handleClearFilters: () => void;
  handleSaveTableView: (id: string, name: string, configuration: TableViewConfiguration) => void;
  handleArchiveTableView: (id: string) => void;
  handleSetCurrentTableView: (id: string) => void;
  handleExportData: (exporterFileType: ExporterFileType) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleDateUpdate: (range: { startDate?: Iso8601DateString; endDate?: Iso8601DateString }) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleDateTypeFilterUpdate: (dateType: 'created_at' | 'resolved_at') => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStateFilterUpdate: (stateIds: FilterValues) => void;
  handleCaseTemplateFilterUpdate: (templateIds: FilterValues) => void;
  handleCaseQueueFilterUpdate: (caseQueueIds: FilterValues) => void;
  handleAssigneeFilterUpdate: (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => void;
  handleNotAssigneeFilterUpdate: (userAssigneeIds: string[]) => void;
  handleTagsConditionsFilterUpdate: (tagsConditions: FilterTagsCondition[][]) => void;
  handleBulkAssign: (taskIds: string[], userId: string, message: string) => void;
  handleBulkSetState: (taskIds: string[], data: BulkSetCaseStateData) => void;
  markAllNotificationsAsReceived: () => void;
  archiveNotification: (notificationId: string) => void;
  unarchiveNotification: (notificationId: string) => void;
  archiveAllNotifications: () => void;
  loadUserNotificationPage: (
    pageNumber: number,
    status: UserNotificationStatus,
    requestKey: RequestKey,
  ) => Promise<void>;
}

const DashboardCasesView = ({
  organization,
  loadingCases,
  loadingTableViews,
  loadingTags,
  loadingCaseTemplateBasicsOptions,
  productFeatureIsDisabled,
  exportersProductFeatureIsDisabled,
  accounts,
  allTags,
  casesTags,
  caseQueues,
  users,
  activeTeams,
  userNotifications,
  cases,
  activeCaseTemplateBasicsOptions,
  caseUsers,
  caseCaseQueues,
  paginationParams,
  sortParams,
  filters,
  pageSize,
  currentTableView,
  activeTableViews,
  impersonationMode,
  hasCasesFree,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  handleCreateCase,
  handleCreateTableView,
  handleClearFilters,
  handleSaveTableView,
  handleSetCurrentTableView,
  handleArchiveTableView,
  handleExportData,
  handlePageLinkClick,
  handlePageSizeClick,
  handleDateUpdate,
  handleDateUpdateV2,
  handleDateTypeFilterUpdate,
  handleOmniSearch,
  handleSortUpdate,
  handleStateFilterUpdate,
  handleCaseTemplateFilterUpdate,
  handleCaseQueueFilterUpdate,
  handleAssigneeFilterUpdate,
  handleNotAssigneeFilterUpdate,
  handleTagsConditionsFilterUpdate,
  handleBulkAssign,
  handleBulkSetState,
  markAllNotificationsAsReceived,
  archiveNotification,
  unarchiveNotification,
  archiveAllNotifications,
  loadUserNotificationPage,
}: DashboardCasesViewProps) => {
  const [createCaseModal, { enable: showCreateCaseModal, disable: hideCreateCaseModal }] =
    useToggle(false);

  // NB: some of our filters do not have an 'empty' state (i.e. filter metadata such as dateType),
  // so we exclude them when determining if filters are empty.
  const nonNullableFilterKeys = ['dateType'];
  const nullableFilters = omitBy(Object(filters), (filterKey: string, _filterValue: never) =>
    nonNullableFilterKeys.includes(filterKey),
  );

  const hasUsedFilters = !Object.values(nullableFilters).every(isEmpty);

  const usedFilters = Object.values(nullableFilters).filter((filter) => !isEmpty(filter));

  const hasFilterOnlyOnOpenCases =
    usedFilters.length === 1 &&
    Object.values(filters?.status || []).length === 1 &&
    Object.values(filters?.status || []).includes(CaseStatus.Open);

  const createCaseEnabled = usePermission(Permission.CaseCreate);

  const handleCreateCaseAndClose = async (values: CreateCaseFormData) => {
    await handleCreateCase(values);
    hideCreateCaseModal();
  };

  if (
    productFeatureIsDisabled ||
    [hasCasesFree, hasCasesBasic, hasCasesEnhanced, hasCasesEnterprise].filter(Boolean).length === 0
  ) {
    return (
      <DisabledProductPage
        id={DISABLED_CASES_PAGE.id}
        title={DISABLED_CASES_PAGE.title}
        headerTitle={DISABLED_CASES_PAGE.headerTitle}
        description={DISABLED_CASES_PAGE.description}
        uses={DISABLED_CASES_PAGE.uses}
        image={DISABLED_CASES_PAGE.image}
      />
    );
  }

  const trackViewUserNotificationMetrics = () => {
    SegmentClient.sendSegmentTrackEvent('Case - View User Notifications', {
      hasNewNotifications:
        (userNotifications[INBOX_REQUEST_KEY]?.paginationParams?.['total-count'] ?? 0) > 0,
      impersonationMode: impersonationMode,
      organizationToken: organization.id,
    });
  };

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <ModalWrapper isOpen={createCaseModal} handleCloseModal={hideCreateCaseModal}>
        <CasesCreateCaseModal
          caseTemplatesMeta={activeCaseTemplateBasicsOptions.map((option) => {
            return { id: option.id, name: option.name, prefix: option['case-prefix'] };
          })}
          submit={handleCreateCaseAndClose}
          handleClose={hideCreateCaseModal}
        />
      </ModalWrapper>
      <Header title='Cases'>
        <HStack>
          <Button
            data-test='cases__create-case-button'
            id='cases__create-case-button'
            name='create-case.button'
            onClick={showCreateCaseModal}
            disabled={!createCaseEnabled}
            prefixIcon={<PlusIcon />}
            variant='basic'
          >
            Create case
          </Button>
          {!exportersProductFeatureIsDisabled && (
            <Permissioned permission={Permission.CasesExport}>
              <DropdownMenu
                trigger={
                  <Button
                    id='cases__export'
                    variant='basic'
                    data-test='cases__export'
                    prefixIcon={<TransferOutIcon />}
                  >
                    Export
                  </Button>
                }
                align='end'
              >
                {Object.values(ExporterFileType).map((fileType) => (
                  <DropdownMenu.Item
                    id={`cases__export__${fileType}`}
                    data-test={`cases__export__${fileType}`}
                    onClick={() => {
                      handleExportData(fileType);
                    }}
                    key={`cases__export__${fileType}`}
                  >
                    {`To ${fileType}`}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu>
            </Permissioned>
          )}
          <UserNotifications
            userNotifications={userNotifications}
            handleMarkAllAsReceived={markAllNotificationsAsReceived}
            handleArchive={archiveNotification}
            handleUnarchive={unarchiveNotification}
            handleMarkAllAsArchived={archiveAllNotifications}
            loadUserNotificationPage={loadUserNotificationPage}
            trackViewUserNotificationMetrics={trackViewUserNotificationMetrics}
          />
        </HStack>
      </Header>
      <Section data-test='cases-view'>
        <ConfigurableCasesTable
          accounts={accounts}
          allTags={allTags}
          cases={cases}
          activeCaseTemplateBasicsOptions={activeCaseTemplateBasicsOptions}
          caseUsers={caseUsers}
          caseCaseQueues={caseCaseQueues}
          caseQueues={caseQueues}
          filters={filters}
          hasFilterOnlyOnOpenCases={hasFilterOnlyOnOpenCases}
          hasUsedFilters={hasUsedFilters}
          loadingCases={loadingCases}
          loadingCaseTemplateBasicsOptions={loadingCaseTemplateBasicsOptions}
          loadingTableViews={loadingTableViews}
          loadingTags={loadingTags}
          pageSize={pageSize}
          paginationParams={paginationParams}
          sortParams={sortParams}
          currentTableView={currentTableView}
          activeTableViews={activeTableViews}
          activeTeams={activeTeams}
          casesTags={casesTags}
          hasCasesBasic={hasCasesBasic}
          hasCasesEnhanced={hasCasesEnhanced}
          hasCasesEnterprise={hasCasesEnterprise}
          users={users}
          handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
          handleNotAssigneeFilterUpdate={handleNotAssigneeFilterUpdate}
          handleBulkAssign={handleBulkAssign}
          handleBulkSetState={handleBulkSetState}
          handleCaseTemplateFilterUpdate={handleCaseTemplateFilterUpdate}
          handleCaseQueueFilterUpdate={handleCaseQueueFilterUpdate}
          handleClearFilters={handleClearFilters}
          handleDateTypeFilterUpdate={handleDateTypeFilterUpdate}
          handleDateUpdate={handleDateUpdate}
          handleDateUpdateV2={handleDateUpdateV2}
          handlePageLinkClick={handlePageLinkClick}
          handlePageSizeClick={handlePageSizeClick}
          handleCreateTableView={handleCreateTableView}
          handleSaveTableView={handleSaveTableView}
          handleSetCurrentTableView={handleSetCurrentTableView}
          handleArchiveTableView={handleArchiveTableView}
          handleSortUpdate={handleSortUpdate}
          handleOmniSearch={handleOmniSearch}
          handleStateFilterUpdate={handleStateFilterUpdate}
          handleTagsConditionsFilterUpdate={handleTagsConditionsFilterUpdate}
        />
      </Section>
    </Container>
  );
};

export default DashboardCasesView;
